import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const STATUS_CONFIG = {
  New: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  'In Discussion': { bg: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  Contacted: { bg: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  'Proposal Sent': { bg: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  Confirmed: { bg: 'bg-teal-50 text-teal-700 border-teal-200', dot: 'bg-teal-500' },
  Closed: { bg: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
};

const formatDateTime = (dateVal) => {
  if (!dateVal) return { date: 'N/A', time: '', full: 'N/A' };
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return { date: 'N/A', time: '', full: 'N/A' };

  const date = d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return { date, time, full: `${date} at ${time}` };
};

const CorporateInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [tripTypeFilter, setTripTypeFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);

  // Modal States
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('corporateInquiryList');
      const data = response?.data || response?.inquiries || [];
      setInquiries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Fetch corporate inquiries error:', error);
      toast.error('Failed to load corporate inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      setIsUpdatingStatus(true);
      const res = await apiService.post('corporateInquiryStatus', {
        id: inquiryId,
        status: newStatus,
      });

      if (res?.status) {
        toast.success(`Status updated to ${newStatus}`);
        setInquiries((prev) =>
          prev.map((item) => (item._id === inquiryId ? { ...item, status: newStatus } : item))
        );
        if (selectedInquiry?._id === inquiryId) {
          setSelectedInquiry((prev) => ({ ...prev, status: newStatus }));
        }
      } else {
        toast.error(res?.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await apiService.get(`deleteCorporateInquiry/${deleteTargetId}`);
      if (res?.status) {
        toast.success('Inquiry deleted successfully');
        setInquiries((prev) => prev.filter((item) => item._id !== deleteTargetId));
        setShowDeleteModal(false);
        setDeleteTargetId(null);
      } else {
        toast.error(res?.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete inquiry');
    }
  };

  // Filter inquiries
  const filteredInquiries = inquiries.filter((item) => {
    const matchesKeyword =
      !keyword ||
      item.companyName?.toLowerCase().includes(keyword.toLowerCase()) ||
      item.fullName?.toLowerCase().includes(keyword.toLowerCase()) ||
      item.workEmail?.toLowerCase().includes(keyword.toLowerCase()) ||
      item.phoneNumber?.includes(keyword) ||
      item.preferredDestination?.toLowerCase().includes(keyword.toLowerCase());

    const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
    const matchesTripType = tripTypeFilter === 'All' || item.tripType === tripTypeFilter;

    return matchesKeyword && matchesStatus && matchesTripType;
  });

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredInquiries.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredInquiries.length / recordsPerPage) || 1;

  // Stats Counters
  const totalCount = inquiries.length;
  const newCount = inquiries.filter((i) => i.status === 'New').length;
  const inDiscussionCount = inquiries.filter((i) => i.status === 'In Discussion' || i.status === 'Contacted').length;
  const proposalSentCount = inquiries.filter((i) => i.status === 'Proposal Sent' || i.status === 'Confirmed').length;

  return (
    <div className="p-3 sm:p-5 bg-[#f8fafc] min-h-screen">
      {/* Sleek Breadcrumb & Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-3 mb-3 border-b border-gray-200/80">
        <div>
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <Link to="/dashboard" className="text-gray-500 hover:text-[#009ef7] transition-colors">
              Home
            </Link>
            <span className="text-gray-300">/</span>
            <span className="text-gray-700 font-medium">Corporate Tours</span>
          </nav>
          <div className="flex items-center gap-2">
            <h1 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight">
              Corporate Inquiries
            </h1>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-sky-50 text-[#009ef7] border border-sky-100">
              {filteredInquiries.length} leads
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            onClick={fetchInquiries}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 text-xs font-semibold shadow-2xs transition-colors"
          >
            <i className={`bi bi-arrow-clockwise ${loading ? 'animate-spin' : ''}`}></i>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Sleek Modern KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 mb-3.5">
        {/* Total Inquiries */}
        <div className="bg-white rounded-xl p-3 border border-gray-200/80 shadow-2xs hover:border-blue-200 transition-colors">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Total Leads</span>
            <span className="w-6 h-6 rounded-md bg-blue-50 text-[#009ef7] flex items-center justify-center text-xs">
              <i className="bi bi-briefcase"></i>
            </span>
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-gray-900">{totalCount}</span>
            <span className="text-[10px] text-gray-400 font-medium">recorded</span>
          </div>
        </div>

        {/* New Requests */}
        <div className="bg-white rounded-xl p-3 border border-gray-200/80 shadow-2xs hover:border-emerald-200 transition-colors">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">New Requests</span>
            <span className="w-6 h-6 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center text-xs">
              <i className="bi bi-bell"></i>
            </span>
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-emerald-700">{newCount}</span>
            {newCount > 0 ? (
              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Action Needed</span>
            ) : (
              <span className="text-[10px] text-gray-400 font-medium">all reviewed</span>
            )}
          </div>
        </div>

        {/* In Discussion */}
        <div className="bg-white rounded-xl p-3 border border-gray-200/80 shadow-2xs hover:border-amber-200 transition-colors">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide">In Discussion</span>
            <span className="w-6 h-6 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center text-xs">
              <i className="bi bi-chat-dots"></i>
            </span>
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-amber-700">{inDiscussionCount}</span>
            <span className="text-[10px] text-gray-400 font-medium">pipeline</span>
          </div>
        </div>

        {/* Proposals / Confirmed */}
        <div className="bg-white rounded-xl p-3 border border-gray-200/80 shadow-2xs hover:border-indigo-200 transition-colors">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wide">Proposals / Won</span>
            <span className="w-6 h-6 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs">
              <i className="bi bi-award"></i>
            </span>
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-indigo-700">{proposalSentCount}</span>
            <span className="text-[10px] text-gray-400 font-medium">advanced</span>
          </div>
        </div>
      </div>

      {/* Sleek Filter Strip */}
      <div className="bg-white rounded-xl p-2.5 sm:p-3 border border-gray-200/80 shadow-2xs mb-3.5">
        <div className="flex flex-col md:flex-row gap-2.5 items-center">
          {/* Keyword Search */}
          <div className="relative flex-1 w-full">
            <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search company, contact person, email, phone, destination..."
              className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#009ef7] focus:ring-1 focus:ring-[#009ef7]/20 transition-all"
            />
          </div>

          {/* Status Filter */}
          <div className="w-full md:w-44">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 focus:outline-none focus:border-[#009ef7] bg-white transition-all"
            >
              <option value="All">All Statuses</option>
              <option value="New">New</option>
              <option value="In Discussion">In Discussion</option>
              <option value="Contacted">Contacted</option>
              <option value="Proposal Sent">Proposal Sent</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Closed">Closed</option>
            </select>
          </div>

          {/* Trip Type Filter */}
          <div className="w-full md:w-56">
            <select
              value={tripTypeFilter}
              onChange={(e) => {
                setTripTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 focus:outline-none focus:border-[#009ef7] bg-white transition-all"
            >
              <option value="All">All Trip Types</option>
              <option value="Annual Team Offsite">Annual Team Offsite</option>
              <option value="Leadership & Executive Retreat">Leadership & Executive Retreat</option>
              <option value="MICE & Corporate Conference">MICE & Corporate Conference</option>
              <option value="Rewards & Incentive Tour">Rewards & Incentive Tour</option>
              <option value="Team Building & Adventure Outing">Team Building & Adventure Outing</option>
              <option value="Workation & Remote Team Gathering">Workation & Remote Team Gathering</option>
            </select>
          </div>

          {/* Reset Filters */}
          {(keyword || statusFilter !== 'All' || tripTypeFilter !== 'All') && (
            <button
              onClick={() => {
                setKeyword('');
                setStatusFilter('All');
                setTripTypeFilter('All');
                setCurrentPage(1);
              }}
              className="px-2.5 py-1.5 text-xs text-rose-600 hover:text-rose-700 font-semibold bg-rose-50 hover:bg-rose-100/70 rounded-lg transition-colors whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-xl border border-gray-200/80 shadow-2xs overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <div className="w-7 h-7 border-2 border-[#009ef7] border-t-transparent rounded-full animate-spin mx-auto mb-2.5"></div>
            <p className="text-xs font-medium text-gray-600">Loading corporate inquiries...</p>
          </div>
        ) : filteredInquiries.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <i className="bi bi-inbox text-3xl mb-1.5 block text-gray-300"></i>
            <h3 className="text-xs font-bold text-gray-700 mb-0.5">No corporate inquiries found</h3>
            <p className="text-[11px] text-gray-400">
              {keyword || statusFilter !== 'All' || tripTypeFilter !== 'All'
                ? 'Try adjusting your search criteria or active filters.'
                : 'Corporate proposal requests submitted from the website will appear here.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-gray-700">
              <thead className="bg-gray-50/90 text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200/80">
                <tr>
                  <th className="py-2.5 px-3.5">Ref # / Date</th>
                  <th className="py-2.5 px-3.5">Organization & Contact</th>
                  <th className="py-2.5 px-3.5">Trip Details</th>
                  <th className="py-2.5 px-3.5">Group / Scope</th>
                  <th className="py-2.5 px-3.5">Status</th>
                  <th className="py-2.5 px-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentRecords.map((item) => {
                  const statusConf = STATUS_CONFIG[item.status] || STATUS_CONFIG.New;
                  const createdObj = formatDateTime(item.createdAt);

                  return (
                    <tr key={item._id} className="hover:bg-slate-50/70 transition-colors">
                      {/* Ref ID & Date / Time */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono font-semibold text-[10.5px]">
                          #TL-CORP-{item.id || item._id?.slice(-5)}
                        </span>
                        <div className="text-[11px] text-gray-700 font-medium mt-1 flex items-center gap-1">
                          <i className="bi bi-calendar3 text-[10px] text-gray-400"></i>
                          <span>{createdObj.date}</span>
                        </div>
                        <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                          <i className="bi bi-clock text-[9.5px]"></i>
                          <span>{createdObj.time}</span>
                        </div>
                      </td>

                      {/* Organization & Contact */}
                      <td className="py-2.5 px-3.5">
                        <div className="font-semibold text-gray-900 text-xs">{item.companyName}</div>
                        <div className="text-gray-600 text-[11px]">
                          {item.fullName}
                          {item.designation ? (
                            <span className="text-gray-400 text-[10.5px]"> • {item.designation}</span>
                          ) : null}
                        </div>
                        <div className="text-[10.5px] text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
                          <a
                            href={`mailto:${item.workEmail}`}
                            className="text-[#009ef7] hover:underline"
                          >
                            {item.workEmail}
                          </a>
                          <span>•</span>
                          <span>{item.phoneNumber}</span>
                        </div>
                      </td>

                      {/* Trip Details */}
                      <td className="py-2.5 px-3.5">
                        <div className="font-semibold text-gray-800 text-xs">{item.tripType}</div>
                        <div className="text-gray-500 text-[11px]">
                          <span className="font-medium text-gray-600">Dest:</span>{' '}
                          {item.preferredDestination || 'Flexible'}
                        </div>
                        {item.departureCity && (
                          <div className="text-[10px] text-gray-400">
                            Origin: {item.departureCity}
                          </div>
                        )}
                      </td>

                      {/* Group / Scope */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        <div className="font-semibold text-gray-800 text-xs">{item.groupSize}</div>
                        <div className="text-[11px] text-gray-500">{item.duration || '2N / 3D'}</div>
                        {item.tentativeDate && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            🗓 {item.tentativeDate}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        <select
                          value={item.status || 'New'}
                          onChange={(e) => handleStatusChange(item._id, e.target.value)}
                          disabled={isUpdatingStatus}
                          className={`text-[11px] font-bold rounded-md px-2 py-0.5 border transition-all cursor-pointer outline-none ${statusConf.bg}`}
                        >
                          <option value="New">New</option>
                          <option value="In Discussion">In Discussion</option>
                          <option value="Contacted">Contacted</option>
                          <option value="Proposal Sent">Proposal Sent</option>
                          <option value="Confirmed">Confirmed</option>
                          <option value="Closed">Closed</option>
                        </select>
                      </td>

                      {/* Actions */}
                      <td className="py-2.5 px-3.5 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1.5">
                          {/* WhatsApp Button */}
                          {item.phoneNumber && (
                            <a
                              href={`https://wa.me/${item.phoneNumber.replace(/\D/g, '')}?text=Hi%20${encodeURIComponent(item.fullName)},%20greetings%20from%20TravellingLark%20Corporate%20Travel.%20We%20received%20your%20offsite%20inquiry%20for%20${encodeURIComponent(item.companyName)}.`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-6.5 h-6.5 rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-colors flex items-center justify-center text-xs"
                              title="Chat on WhatsApp"
                            >
                              <i className="bi bi-whatsapp"></i>
                            </a>
                          )}

                          {/* View Details */}
                          <button
                            onClick={() => {
                              setSelectedInquiry(item);
                              setShowDetailModal(true);
                            }}
                            className="w-6.5 h-6.5 rounded-md bg-sky-50 text-[#009ef7] hover:bg-[#009ef7] hover:text-white transition-colors flex items-center justify-center text-xs"
                            title="View Full Details"
                          >
                            <i className="bi bi-eye"></i>
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => {
                              setDeleteTargetId(item._id);
                              setShowDeleteModal(true);
                            }}
                            className="w-6.5 h-6.5 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-colors flex items-center justify-center text-xs"
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Strip */}
        {filteredInquiries.length > recordsPerPage && (
          <div className="px-3.5 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <div>
              Showing <span className="font-semibold text-gray-700">{indexOfFirstRecord + 1}</span> to{' '}
              <span className="font-semibold text-gray-700">{Math.min(indexOfLastRecord, filteredInquiries.length)}</span> of{' '}
              <span className="font-semibold text-gray-700">{filteredInquiries.length}</span> leads
            </div>
            <div className="flex items-center gap-1">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="px-2.5 py-1 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .slice(Math.max(0, currentPage - 3), currentPage + 2)
                .map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-6.5 h-6.5 rounded-md font-semibold text-xs ${
                      currentPage === pageNum
                        ? 'bg-[#009ef7] text-white shadow-2xs'
                        : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="px-2.5 py-1 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* VIEW DETAILS MODAL */}
      {showDetailModal && selectedInquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-xl w-full p-4 sm:p-5 shadow-2xl border border-gray-200 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2.5 mb-3.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2 py-0.5 rounded bg-sky-50 text-[#009ef7] text-xs font-mono font-bold">
                  #TL-CORP-{selectedInquiry.id || selectedInquiry._id?.slice(-5)}
                </span>
                <span
                  className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                    (STATUS_CONFIG[selectedInquiry.status] || STATUS_CONFIG.New).bg
                  }`}
                >
                  {selectedInquiry.status}
                </span>
                <span className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200/80 px-2 py-0.5 rounded-md flex items-center gap-1.5 font-medium">
                  <i className="bi bi-clock-history text-[#009ef7]"></i>
                  <span>Created: {formatDateTime(selectedInquiry.createdAt).date} at {formatDateTime(selectedInquiry.createdAt).time}</span>
                </span>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors text-xs"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="space-y-3 text-xs">
              {/* Organization Card */}
              <div className="bg-gray-50/80 rounded-lg p-3 border border-gray-100">
                <h4 className="font-bold text-sm text-gray-900 mb-1.5">
                  {selectedInquiry.companyName}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-600 text-xs">
                  <div>
                    <span className="text-gray-400">Representative:</span>{' '}
                    <strong className="text-gray-800">{selectedInquiry.fullName}</strong>
                  </div>
                  <div>
                    <span className="text-gray-400">Designation:</span>{' '}
                    <strong className="text-gray-800">{selectedInquiry.designation || 'N/A'}</strong>
                  </div>
                  <div>
                    <span className="text-gray-400">Work Email:</span>{' '}
                    <a href={`mailto:${selectedInquiry.workEmail}`} className="text-[#009ef7] hover:underline">
                      {selectedInquiry.workEmail}
                    </a>
                  </div>
                  <div>
                    <span className="text-gray-400">Phone:</span>{' '}
                    <strong className="text-gray-800">{selectedInquiry.phoneNumber}</strong>
                  </div>
                </div>
              </div>

              {/* Trip Scope */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div className="p-2.5 bg-white border border-gray-200 rounded-lg">
                  <span className="text-gray-400 block text-[10px] uppercase font-semibold">Trip Type</span>
                  <span className="font-semibold text-gray-800 mt-0.5 block">{selectedInquiry.tripType}</span>
                </div>
                <div className="p-2.5 bg-white border border-gray-200 rounded-lg">
                  <span className="text-gray-400 block text-[10px] uppercase font-semibold">Group Size</span>
                  <span className="font-semibold text-gray-800 mt-0.5 block">{selectedInquiry.groupSize}</span>
                </div>
                <div className="p-2.5 bg-white border border-gray-200 rounded-lg">
                  <span className="text-gray-400 block text-[10px] uppercase font-semibold">Duration</span>
                  <span className="font-semibold text-gray-800 mt-0.5 block">{selectedInquiry.duration || '2N / 3D'}</span>
                </div>
                <div className="p-2.5 bg-white border border-gray-200 rounded-lg">
                  <span className="text-gray-400 block text-[10px] uppercase font-semibold">Destination</span>
                  <span className="font-semibold text-gray-800 mt-0.5 block">{selectedInquiry.preferredDestination || 'Flexible'}</span>
                </div>
                <div className="p-2.5 bg-white border border-gray-200 rounded-lg">
                  <span className="text-gray-400 block text-[10px] uppercase font-semibold">Departure City</span>
                  <span className="font-semibold text-gray-800 mt-0.5 block">{selectedInquiry.departureCity || 'N/A'}</span>
                </div>
                <div className="p-2.5 bg-white border border-gray-200 rounded-lg">
                  <span className="text-gray-400 block text-[10px] uppercase font-semibold">Tentative Date</span>
                  <span className="font-semibold text-gray-800 mt-0.5 block">{selectedInquiry.tentativeDate || 'Flexible'}</span>
                </div>
                <div className="p-2.5 bg-sky-50/50 border border-sky-100 rounded-lg col-span-2 sm:col-span-3 flex items-center justify-between">
                  <div>
                    <span className="text-gray-400 block text-[10px] uppercase font-semibold">Inquiry Created Timestamp</span>
                    <span className="font-bold text-gray-800 mt-0.5 flex items-center gap-2 text-xs">
                      <span className="flex items-center gap-1">
                        <i className="bi bi-calendar3 text-[#009ef7]"></i>
                        <span>{formatDateTime(selectedInquiry.createdAt).date}</span>
                      </span>
                      <span className="text-gray-300">•</span>
                      <span className="flex items-center gap-1">
                        <i className="bi bi-clock text-amber-500"></i>
                        <span>{formatDateTime(selectedInquiry.createdAt).time}</span>
                      </span>
                    </span>
                  </div>
                  <span className="text-[10.5px] font-semibold text-sky-700 bg-white border border-sky-200/80 px-2 py-0.5 rounded-md shadow-2xs">
                    Website Lead
                  </span>
                </div>
              </div>

              {/* Special Requirements */}
              {selectedInquiry.requirements && (
                <div className="bg-sky-50/50 border border-sky-100 rounded-lg p-3">
                  <span className="text-[10.5px] font-semibold text-gray-700 block mb-1">
                    Requirements &amp; Specific Notes:
                  </span>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-xs">
                    {selectedInquiry.requirements}
                  </p>
                </div>
              )}

              {/* Quick Status Updater */}
              <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600">Update Lead Status:</span>
                <select
                  value={selectedInquiry.status}
                  onChange={(e) => handleStatusChange(selectedInquiry._id, e.target.value)}
                  className="px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-semibold text-gray-800 focus:outline-none"
                >
                  <option value="New">New</option>
                  <option value="In Discussion">In Discussion</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Proposal Sent">Proposal Sent</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-2.5">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-3.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="relative bg-white rounded-2xl w-full max-w-[460px] p-6 sm:p-7 shadow-2xl border border-slate-100 text-center animate-in fade-in zoom-in-95 duration-200">
            {/* Top Close Button */}
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteTargetId(null);
              }}
              className="absolute top-4 right-4 w-8 h-8 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-colors text-xs"
              title="Close"
            >
              <i className="bi bi-x-lg"></i>
            </button>

            {/* Modern Warning Icon */}
            <div className="w-16 h-16 rounded-2xl bg-rose-50 text-rose-600 border border-rose-100/80 flex items-center justify-center mx-auto mb-4.5 shadow-sm">
              <i className="bi bi-exclamation-triangle text-2xl"></i>
            </div>

            {/* Title & Body */}
            <h3 className="text-lg font-bold text-slate-900 tracking-tight mb-2">
              Delete Corporate Inquiry?
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed max-w-sm mx-auto mb-6">
              Are you sure you want to delete this proposal inquiry? This record will be removed from your active leads list.
            </p>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteTargetId(null);
                }}
                className="w-full py-2.5 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-semibold transition-all shadow-2xs"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="w-full py-2.5 px-4 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm font-semibold transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-1.5"
              >
                <i className="bi bi-trash text-xs"></i>
                <span>Yes, Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateInquiries;
